// src/core/ai/subscription.js
// ============================================================
// مدیریت اشتراک و مصرف روزانه AI Chat — Server-backed
// ============================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const PLAN_LIMITS = {
  simple:   { messages: 20,  images: 1,  name: 'ساده' },
  pro:      { messages: 50,  images: 3,  name: 'پرو' },
  ultimate: { messages: 300, images: 10, name: 'ویژه' },
};

export const PLAN_PRICES = {
  pro: {
    daily:   { amount:  20000, days: 1,   label: 'روزانه' },
    monthly: { amount: 199000, days: 30,  label: 'ماهانه' },
    yearly:  { amount:1790000, days: 365, label: 'سالانه' },
  },
  ultimate: {
    daily:   { amount:  50000, days: 1,   label: 'روزانه' },
    monthly: { amount: 499000, days: 30,  label: 'ماهانه' },
    yearly:  { amount:4490000, days: 365, label: 'سالانه' },
  },
};

// ============================================================
// Helpers
// ============================================================
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function blankUsage() {
  return {
    date: todayKey(),
    messages: { simple: 0, pro: 0, ultimate: 0 },
    images:   { simple: 0, pro: 0, ultimate: 0 },
  };
}

// ============================================================
// Subscription (سرور: /aiSubscriptions)
// ============================================================
export async function getSubscription(userId) {
  if (!userId) return null;
  try {
    const res = await fetch(`${API_BASE}/aiSubscriptions?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    const sub = Array.isArray(data) ? data[0] : null;
    if (!sub) return null;

    // چک انقضا
    if (!sub.expiresAt || Date.now() > sub.expiresAt) {
      try { await fetch(`${API_BASE}/aiSubscriptions/${sub.id}`, { method: 'DELETE' }); } catch {}
      return null;
    }
    return sub;
  } catch (e) {
    console.warn('⚠️ subscription: getSubscription failed', e);
    return null;
  }
}

export async function setSubscription(userId, sub) {
  if (!userId || !sub) return null;
  try {
    const res = await fetch(`${API_BASE}/aiSubscriptions?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    const existing = Array.isArray(data) ? data[0] : null;

    const payload = {
      ...sub,
      userId,
      updatedAt: new Date().toISOString(),
    };

    if (existing) {
      const r = await fetch(`${API_BASE}/aiSubscriptions/${existing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return r.ok ? await r.json() : null;
    } else {
      const r = await fetch(`${API_BASE}/aiSubscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return r.ok ? await r.json() : null;
    }
  } catch (e) {
    console.warn('⚠️ subscription: setSubscription failed', e);
    return null;
  }
}

export async function clearSubscription(userId) {
  if (!userId) return;
  try {
    const res = await fetch(`${API_BASE}/aiSubscriptions?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    if (Array.isArray(data)) {
      await Promise.all(
        data.map((s) => fetch(`${API_BASE}/aiSubscriptions/${s.id}`, { method: 'DELETE' }).catch(() => {}))
      );
    }
  } catch {}
}

export async function hasActivePlan(userId, plan) {
  if (plan === 'simple') return true;
  const sub = await getSubscription(userId);
  return !!sub && sub.plan === plan;
}

// ============================================================
// Usage (سرور: /aiUsage)
// ============================================================
export async function getUsage(userId) {
  const blank = blankUsage();
  if (!userId) return blank;
  try {
    const res = await fetch(`${API_BASE}/aiUsage?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    const rec = Array.isArray(data) ? data[0] : null;
    if (!rec) return blank;

    // اگر روز عوض شده → صفر
    if (rec.date !== todayKey()) return blank;

    return {
      ...rec,
      messages: rec.messages || { simple: 0, pro: 0, ultimate: 0 },
      images:   rec.images   || { simple: 0, pro: 0, ultimate: 0 },
    };
  } catch (e) {
    console.warn('⚠️ subscription: getUsage failed', e);
    return blank;
  }
}

export async function saveUsage(userId, usage) {
  if (!userId || !usage) return;
  try {
    const res = await fetch(`${API_BASE}/aiUsage?userId=${encodeURIComponent(userId)}`);
    const data = await res.json();
    const rec = Array.isArray(data) ? data[0] : null;

    const payload = {
      ...usage,
      userId,
      updatedAt: new Date().toISOString(),
    };

    if (rec) {
      await fetch(`${API_BASE}/aiUsage/${rec.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } else {
      await fetch(`${API_BASE}/aiUsage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    }
  } catch (e) {
    console.warn('⚠️ subscription: saveUsage failed', e);
  }
}

// ============================================================
// Remaining (Pure — بدون side effect)
// ============================================================
export function remainingMessages(plan, usage) {
  const limit = PLAN_LIMITS[plan]?.messages ?? 0;
  const used = usage?.messages?.[plan] ?? 0;
  return Math.max(0, limit - used);
}

export function remainingImages(plan, usage) {
  const limit = PLAN_LIMITS[plan]?.images ?? 0;
  const used = usage?.images?.[plan] ?? 0;
  return Math.max(0, limit - used);
}

// ============================================================
// Increment (async)
// ============================================================
export async function incrementMessage(userId, plan) {
  const u = await getUsage(userId);
  u.messages = u.messages || { simple: 0, pro: 0, ultimate: 0 };
  u.messages[plan] = (u.messages[plan] || 0) + 1;
  await saveUsage(userId, u);
  return u;
}

export async function incrementImage(userId, plan) {
  const u = await getUsage(userId);
  u.images = u.images || { simple: 0, pro: 0, ultimate: 0 };
  u.images[plan] = (u.images[plan] || 0) + 1;
  await saveUsage(userId, u);
  return u;
}

// ============================================================
// Helper: خالی
// ============================================================
export function createBlankUsage() {
  return blankUsage();
}